<script setup lang="ts">
// Global search (plan 2E R15; `searchSheet`, line 2190): one request to each module the
// viewer may search, debounced, with results as they land. A module that fails is named
// and the others still show. A new query cancels what's in flight, so a slow old answer
// can't replace a new one. Each keystroke past the debounce reads whole collections in
// up to five modules, which is fine at Sierra's scale.
import { SEARCH_MIN, hitSub, topHits, type SearchHit } from '@itc/shared';
import { useUser, visibleSearches } from '@itc/sdk';
import { Banner, EmptyState, Input, Modal, Tag } from '@itc/ui';
import { computed, nextTick, ref, shallowRef, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';
import { modules } from '../modules.ts';

const DEBOUNCE_MS = 250;

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: [] }>();

const user = useUser();
const router = useRouter();
const query = ref('');
const found = shallowRef<Readonly<Record<string, readonly SearchHit[]>>>({});
const failed = ref<string[]>([]);
const searching = ref(false);
const box = useTemplateRef<HTMLElement>('box');
let timer: ReturnType<typeof setTimeout> | undefined;
let running: AbortController | undefined;

const hits = computed(() => topHits(Object.values(found.value).flat()));
const asked = computed(() => query.value.trim().length >= SEARCH_MIN);
const when = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

function reset(): void {
  clearTimeout(timer);
  running?.abort();
  found.value = {};
  failed.value = [];
  searching.value = false;
}

async function run(q: string): Promise<void> {
  running?.abort();
  const controller = new AbortController();
  running = controller;
  found.value = {};
  failed.value = [];
  searching.value = true;
  await Promise.all(
    visibleSearches(modules, (permission) => user.can(permission)).map(async ({ module, title, search }) => {
      try {
        const landed = await search.run(q, controller.signal);
        if (!controller.signal.aborted) found.value = { ...found.value, [module]: landed };
      } catch {
        if (!controller.signal.aborted) failed.value = [...failed.value, title];
      }
    }),
  );
  if (!controller.signal.aborted) searching.value = false;
}

watch(query, (value) => {
  reset();
  const q = value.trim();
  if (q.length >= SEARCH_MIN) timer = setTimeout(() => void run(q), DEBOUNCE_MS);
});

watch(
  () => props.open,
  async (open) => {
    reset();
    if (!open) return;
    query.value = '';
    await nextTick();
    box.value?.querySelector('input')?.focus();
  },
);

async function go(hit: SearchHit): Promise<void> {
  emit('close');
  await router.push(hit.link);
}
</script>

<template>
  <Modal :open="open" title="Search" wide @close="emit('close')">
    <div ref="box" class="search__box">
      <Input v-model="query" type="search" placeholder="Ops, people, items, ships, requests, donations…" aria-label="Search everything" autocomplete="off" data-testid="search-input" />
    </div>
    <Banner v-if="failed.length > 0" tone="warn">Couldn’t search {{ failed.join(', ') }}.</Banner>
    <p v-if="!asked" class="search__muted">Start typing to search everything you can see.</p>
    <EmptyState v-else-if="hits.length === 0 && !searching" :title="`Nothing matches “${query.trim()}”.`" />
    <ul v-else class="search__hits">
      <li v-for="hit in hits" :key="`${hit.type}:${hit.link}:${hit.title}`">
        <button type="button" class="search__hit" data-testid="search-hit" @click="go(hit)">
          <Tag variant="plain">{{ hit.type }}</Tag>
          <span class="search__text">
            <b>{{ hit.title }}</b>
            <span v-if="hitSub(hit, when) !== ''" class="search__muted">{{ hitSub(hit, when) }}</span>
          </span>
        </button>
      </li>
    </ul>
  </Modal>
</template>

<style scoped>
.search__box {
  margin-bottom: var(--itc-s3);
}
.search__muted {
  margin: 0;
  color: var(--itc-muted);
  font-size: 13px;
}
.search__hits {
  display: grid;
  gap: var(--itc-s1);
  margin: 0;
  padding: 0;
  list-style: none;
}
.search__hit {
  display: flex;
  align-items: flex-start;
  gap: var(--itc-s2);
  width: 100%;
  min-height: var(--itc-tap);
  padding: var(--itc-s2);
  border: 1px solid var(--itc-line);
  border-radius: var(--itc-r-control);
  background: var(--itc-panel-2);
  color: var(--itc-fg);
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.search__hit:hover {
  border-color: var(--itc-accent);
}
.search__text {
  display: grid;
  min-width: 0;
  overflow-wrap: anywhere;
}
</style>
